import { HttpClient } from '@angular/common/http';
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { LicenseEntity, ProductNames } from '../../../dave-data-module/entities/license.entity';
import { PartnerEntity } from '../../../dave-data-module/entities/partner.entity';
import { UserEntity, UserRoles } from '../../../dave-data-module/entities/user.entity';
import { ComponentCanDeactivate } from '../../../dave-data-module/guards/pending-changes.guard';
import { State } from '../../../dave-data-module/State';
import { UsersActionTypes } from '../../../dave-data-module/State/actions/users.actions';
import { getToken } from '../../../dave-data-module/State/selectors/base.selectors';
import { getPartners } from '../../../dave-data-module/State/selectors/partners.selectors';
import { getManagedUsers, getUser } from '../../../dave-data-module/State/selectors/users.selectors';
import { RoutingHistoryService } from '../../../dave-utils-module/dave-shared-components-module/services/routing-history.service';
import { userRequiredFieldValidator } from '../../../helper/validation.helper';

@Component({
    selector: 'app-new-user',
    templateUrl: './new-user.component.html',
    styleUrls: ['./new-user.component.scss'],
})
export class NewUserComponent implements OnInit, OnDestroy, ComponentCanDeactivate {
    public PartnerList: PartnerEntity[] = null;
    public User: UserEntity = null;
    public LicenseList: LicenseEntity[] = null;
    public FilteredLicenseList: LicenseEntity[] = null;
    public UserForm: UntypedFormGroup = new UntypedFormGroup(
        {
            Firstname: new UntypedFormControl(''),
            Lastname: new UntypedFormControl(''),
            Email: new UntypedFormControl('', [Validators.email, Validators.required]),
            Partner: new UntypedFormControl('', Validators.required),
        },
        userRequiredFieldValidator,
    );
    // Observables
    private subscriptions: Subscription[] = [];
    private user$: Observable<UserEntity>;
    private manageUsers$: Observable<UserEntity[]>;
    private partners$: Observable<PartnerEntity[]>;
    private token$: Observable<string>;
    private licenses$: Observable<LicenseEntity[]>;
    private manageUserList: UserEntity[] = null;
    private token: string = null;

    constructor(
        private store: Store<State>,
        private sanatize: DomSanitizer,
        private http: HttpClient,
        private route: ActivatedRoute,
        private routingHistoryService: RoutingHistoryService,
    ) {
        this.manageUsers$ = store.select(getManagedUsers);
        this.partners$ = store.select(getPartners);
        this.token$ = store.select(getToken);
        this.user$ = store.select(getUser);
        this.licenses$ = this.store.select(state => state.licenses.licensList);
    }

    get Firstname() {
        return this.UserForm.get('Firstname');
    }

    get Lastname() {
        return this.UserForm.get('Lastname');
    }

    get Email() {
        return this.UserForm.get('Email');
    }

    get Partner() {
        return this.UserForm.get('Partner');
    }

    ngOnInit() {
        this.subscriptions.push(
            combineLatest([this.partners$, this.token$, this.user$, this.licenses$]).subscribe(
                ([partners, token, user, licenses]) => {
                    this.PartnerList = partners;
                    this.token = token;
                    this.User = user;
                    this.LicenseList = licenses;
                    this.isReadyToShow();
                },
            ),
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(value => value.unsubscribe());
    }

    public Cancel() {
        this.NavigateUp();
    }

    public Submit() {
        if (this.UserForm.invalid) {
            return;
        }
        const { Firstname, Lastname, Email, Partner } = this.UserForm.value;
        const productIds: number[] = [];
        if (this.FilteredLicenseList.find(l => l.ProductName === ProductNames.Basis)) {
            productIds.push(this.FilteredLicenseList.find(l => l.ProductName === ProductNames.Basis).ProductId);
        }
        this.store.dispatch(
            UsersActionTypes.AddManageUser({
                Payload: {
                    firstname: Firstname,
                    lastname: Lastname,
                    email: Email,
                    partnerIds: [Number(Partner.Id)],
                    productIds: productIds,
                },
            }),
        );
        this.NavigateUp();
    }

    public NavigateUp() {
        this.UserForm.reset(this.UserForm.value);
        this.routingHistoryService.NavigateBackwards();
    }

    private isReadyToShow() {
        let partnerId: number = +this.route.snapshot.paramMap.get('partnerId');
        if (!(partnerId > 0)) {
            partnerId = this.User.PartnerId;
        }
        const partner = this.PartnerList.find(value => Number(value.Id) === Number(partnerId));
        this.Partner.setValue(partner);
        this.FilteredLicenseList = this.LicenseList.filter(l => l.PartnerId === partnerId);
    }
    @HostListener('window:beforeunload')
    // tslint:disable-next-line:naming-convention
    canDeactivate(): Observable<boolean> | boolean {
        return !this.UserForm.dirty;
    }
}
